import type { SkillData } from '../spacetime/modules/SkillModule';
import type { AppearanceCatalogEntry, ItemCatalogEntry, MobCatalogEntry } from '../spacetime/types';
import { t } from '../i18n';
import { createWindowFrame, windowId } from './windowing';
import { CodexPanel } from './CodexPanel';
import { buildCodexTabDefinitions, type CodexTab } from './codexShared';
import { buildSkillBarSignature, buildSkillListSignature } from './skillListStability';

interface PickOptions {
  title?: string;
}

export class CodexPicker {
  private readonly container: HTMLDivElement;
  private readonly titleEl: HTMLDivElement;
  private readonly contentArea: HTMLDivElement;
  private readonly closeButton: HTMLButtonElement;
  private readonly frame: ReturnType<typeof createWindowFrame>;
  private readonly panel: CodexPanel;
  private playerSkills: SkillData[] = [];
  private items: ItemCatalogEntry[] = [];
  private mobs: MobCatalogEntry[] = [];
  private appearances: AppearanceCatalogEntry[] = [];
  private skillBarSkillIds: string[] = [];
  private playerSkillsSignature = '';
  private skillBarSkillIdsSignature = '';
  private pendingResolve: ((value: string | null) => void) | null = null;
  private readonly defaultSize = { width: 420, height: 420 };
  private activeKind: CodexTab = 'skills';

  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'window-container';
    this.container.hidden = true;
    this.container.style.position = 'fixed';
    this.container.style.zIndex = '5200';

    const header = document.createElement('div');
    header.className = 'window-header';

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'window-header-title';
    this.titleEl.textContent = t('codex.pickerTitle');

    const resizeHandle = document.createElement('div');
    resizeHandle.className = 'window-resize-handle';
    resizeHandle.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" style="pointer-events: none;"><path d="M0 0 L10 0 L10 10 Z" fill="#9ca3af" /></svg>';

    this.closeButton = document.createElement('button');
    this.closeButton.type = 'button';
    this.closeButton.className = 'inventory-close';
    this.closeButton.textContent = '×';

    header.appendChild(this.titleEl);
    header.appendChild(resizeHandle);
    header.appendChild(this.closeButton);

    this.contentArea = document.createElement('div');
    this.contentArea.className = 'window-content';

    this.container.appendChild(header);
    this.container.appendChild(this.contentArea);
    document.body.appendChild(this.container);

    this.frame = createWindowFrame({
      windowId: windowId('codex-picker', 'main'),
      container: this.container,
      header,
      resizeHandle,
      closeButton: this.closeButton,
      minWidth: 320,
      minHeight: 280,
      initialPosition: {
        x: Math.max(0, Math.round((window.innerWidth - this.defaultSize.width) / 2)),
        y: Math.max(0, Math.round((window.innerHeight - this.defaultSize.height) / 2)),
      },
      initialSize: this.defaultSize,
      persistState: false,
    });
    this.panel = new CodexPanel({
      contentArea: this.contentArea,
      initialTab: this.activeKind,
      getTabs: () => this.buildPickerTabs(),
      onActiveTabChange: (tab) => {
        this.activeKind = tab;
        this.frame.setActiveTab(tab);
      },
    });
    header.after(this.panel.getTabBarElement());
    this.closeButton.addEventListener('click', () => this.close(null));
    this.frame.setWindowState('closed');
  }

  public updateSkills(skills: SkillData[]): void {
    const nextSignature = buildSkillListSignature(skills);
    if (nextSignature === this.playerSkillsSignature) {
      return;
    }
    this.playerSkills = skills;
    this.playerSkillsSignature = nextSignature;
    if (!this.container.hidden) this.panel.render();
  }

  public updateSkillBar(skillIds: string[]): void {
    const nextSignature = buildSkillBarSignature(skillIds);
    if (nextSignature === this.skillBarSkillIdsSignature) {
      return;
    }
    this.skillBarSkillIds = skillIds;
    this.skillBarSkillIdsSignature = nextSignature;
    if (!this.container.hidden) this.panel.render();
  }

  public updateItems(items: ItemCatalogEntry[]): void {
    this.items = items;
    if (!this.container.hidden) this.panel.render();
  }

  public updateMobs(mobs: MobCatalogEntry[]): void {
    this.mobs = mobs;
    if (!this.container.hidden) this.panel.render();
  }

  public updateAppearances(appearances: AppearanceCatalogEntry[]): void {
    this.appearances = appearances;
    if (!this.container.hidden) this.panel.render();
  }

  public pick(kind: CodexTab, options: PickOptions = {}): Promise<string | null> {
    this.close(null);
    this.titleEl.textContent = options.title ?? t('codex.pickerTitle');
    this.activeKind = kind;
    this.panel.setActiveTab(kind);
    this.container.hidden = false;
    this.frame.setWindowState('open');
    this.panel.render();
    return new Promise<string | null>((resolve) => {
      this.pendingResolve = resolve;
    });
  }

  public destroy(): void {
    this.close(null);
    this.panel.destroy();
    this.frame.destroy();
    this.container.remove();
    if (activeCodexPicker === this) {
      activeCodexPicker = null;
    }
  }

  private close(value: string | null): void {
    if (!this.container.hidden) {
      this.container.hidden = true;
      this.frame.setWindowState('closed');
    }
    const resolve = this.pendingResolve;
    this.pendingResolve = null;
    resolve?.(value);
  }

  private buildPickerTabs() {
    const tabs = buildCodexTabDefinitions({
      playerSkills: this.playerSkills,
      items: this.items,
      mobs: this.mobs,
      appearances: this.appearances,
      skillBarSkillIds: this.skillBarSkillIds,
    });
    const selectedTab = tabs.find((tab) => tab.key === this.activeKind);
    if (!selectedTab) {
      throw new Error(`Unknown codex picker tab: ${this.activeKind}`);
    }
    return [{
      ...selectedTab,
      getRows: () => selectedTab.getRows().map((row) => ({
        ...row,
        onDoubleClick: (value: string) => this.close(value),
      })),
    }];
  }
}

let activeCodexPicker: CodexPicker | null = null;

export function createCodexPicker(): CodexPicker {
  if (activeCodexPicker) {
    return activeCodexPicker;
  }
  activeCodexPicker = new CodexPicker();
  return activeCodexPicker;
}
